import type { SweTokenBreakdown } from "@/lib/api/types";
import { dashboardContent, fmt } from "@/lib/content";
import { fmtCompact, fmtSaved, tokenBars, type TokenBar } from "@/lib/dashboard/miner";
import { cn } from "@/lib/utils";

/**
 * Miner against baseline, as bars: the grey track is what the baseline used, the dark bar
 * what the miner used, on one scale for all four rows. Where the miner went over, the part
 * past the baseline mark turns red - the one place on the chart that needs attention.
 * Every figure is printed beside its bar, so nothing depends on reading a length.
 *
 * Where the competition lets miners call JEV, a fifth row shows what they sent it. It has
 * no bar: baseline never calls JEV, so there is nothing to measure it against. The cost is
 * already inside the weighted figure, which says so.
 *
 * Used for the miner's totals and again inside an expanded task: same question, two scales,
 * one unit - the weighted figure on top, raw tokens in the rows below it.
 */
export function TokenBars({ tokens, scale = "miner" }: { tokens: SweTokenBreakdown; scale?: "miner" | "task" }) {
  const t = dashboardContent.miner.tokens;
  const bars = tokenBars(tokens);
  const jev = tokens.miner_jev_input_tokens;
  const hasJev = typeof jev === "number";
  // A miner that never called JEV still gets the row (it says 0), but its weighted figure
  // has nothing of JEV in it, so the note would be wrong there.
  const usedJev = hasJev && jev > 0;
  const labels: Record<TokenBar["id"], string> = {
    weighted: scale === "task" ? t.taskWeighted : t.weighted,
    input: t.input,
    cache: t.cache,
    output: t.output,
  };

  if (bars.every((b) => b.miner === null && b.baseline === null)) {
    return <p className="text-[14px] text-fg-muted">{t.empty}</p>;
  }

  return (
    <div className="grid gap-4">
      {bars.map((bar, i) => (
        <div
          key={bar.id}
          className={cn(
            "grid items-center gap-x-6 gap-y-2 sm:grid-cols-[120px_minmax(0,1fr)_minmax(200px,auto)]",
            // Weighted is the figure the score uses; the three below say where it came from.
            i === 0 && "border-b border-line pb-4",
          )}
        >
          <span className="eyebrow text-[10.5px] text-fg-muted">
            {labels[bar.id]}
            {bar.id === "weighted" && usedJev && <span className="block text-fg-faint">{t.withJev}</span>}
          </span>
          <Track bar={bar} />
          <span className="flex items-baseline justify-between gap-4 text-[13px] tabular-nums sm:justify-end">
            <span className="text-fg-muted">
              <span className="text-fg">{fmtCompact(bar.miner)}</span>{" "}
              {fmt(t.ofBaseline, { baseline: fmtCompact(bar.baseline) })}
            </span>
            {bar.saved !== null && (
              <span className={cn("min-w-[7ch] text-right", bar.saved >= 0 ? "text-ok" : "text-accent")}>
                {fmtSaved(bar.saved)}
              </span>
            )}
          </span>
        </div>
      ))}
      {hasJev && (
        <div className="grid items-center gap-x-6 gap-y-2 border-t border-line-soft pt-4 sm:grid-cols-[120px_minmax(0,1fr)_minmax(200px,auto)]">
          <span className="eyebrow text-[10.5px] text-fg-muted">{t.jev}</span>
          <span className="text-[12px] text-fg-faint">{t.jevNote}</span>
          <span className="text-[13px] text-fg tabular-nums sm:text-right">{fmtCompact(jev)}</span>
        </div>
      )}
    </div>
  );
}

function Track({ bar }: { bar: TokenBar }) {
  return (
    <span aria-hidden className="relative block h-2">
      {/* Baseline: the track up to its mark. */}
      <span className="absolute inset-y-0 left-0 rounded-full bg-line" style={{ width: `${bar.baselinePct}%` }} />
      {/* Miner: up to the baseline in ink, any overshoot in accent. */}
      <span
        className="absolute inset-y-0 left-0 rounded-full bg-fg-2"
        style={{ width: `${bar.inkPct}%` }}
      />
      {bar.overPct > 0 && (
        <span
          className="absolute inset-y-0 rounded-r-full bg-accent"
          style={{ left: `${bar.baselinePct}%`, width: `${bar.overPct}%` }}
        />
      )}
      <span className="absolute -inset-y-1 w-px bg-fg-muted" style={{ left: `${bar.baselinePct}%` }} />
    </span>
  );
}

/** The key for the two marks, set beside the section title. */
export function TokenLegend() {
  const t = dashboardContent.miner.tokens;
  return (
    <span className="flex items-center gap-4 text-[12px] text-fg-muted">
      <span className="flex items-center gap-1.5">
        <span aria-hidden className="h-2 w-4 rounded-full bg-fg-2" />
        {t.miner}
      </span>
      <span className="flex items-center gap-1.5">
        <span aria-hidden className="relative h-2 w-4 rounded-full bg-line">
          <span className="absolute -inset-y-1 right-0 w-px bg-fg-muted" />
        </span>
        {t.baseline}
      </span>
    </span>
  );
}
